import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Action, Store} from "@ngrx/store";
import * as fromRoot from '../../../../state/app.state';
import {GroupDetailsService} from './group-details.service';
import {
  AddGroupDetailsFailure,
  AddGroupDetailsRequest,
  AddGroupDetailsSuccess,
  CreateGroupFailure,
  CreateGroupRequest,
  CreateGroupSuccess,
  DeleteGroupFailure,
  DeleteGroupRequest,
  DeleteGroupSuccess,
  FetchClientDetailsFailure,
  FetchClientDetailsRequest,
  FetchClientDetailsSuccess,
  FetchClientFilterFailure,
  FetchClientFilterRequest,
  FetchClientFilterSuccess,
  FetchCoachesFilterFailure,
  FetchCoachesFilterRequest,
  FetchCoachesFilterSuccess,
  FetchGroupDetailsFailure,
  FetchGroupDetailsRequest,
  FetchGroupDetailsSuccess,
  FetchGroupNameFailure,
  FetchGroupNameRequest,
  FetchGroupNameSuccess,
  FetchGroupStatusFilterFailure,
  FetchGroupStatusFilterRequest,
  FetchGroupStatusFilterSuccess,
  FetchMeasurementsFilterFailure,
  FetchMeasurementsFilterRequest,
  FetchMeasurementsFilterSuccess,
  FetchSessionListFailure,
  FetchSessionListRequest,
  FetchSessionListSuccess,
  GenerateGroupNameFailure,
  GenerateGroupNameRequest,
  GenerateGroupNameSuccess,
  SetDuplicateGroupFailure,
  SetDuplicateGroupRequest,
  SetDuplicateGroupSuccess,
  UpdateGroupDetailsFailure,
  UpdateGroupDetailsRequest,
  UpdateGroupDetailsSuccess,
  VerifyGroupNameFailure,
  VerifyGroupNameRequest,
  VerifyGroupNameSuccess
} from './group-details.actions';
import {Observable, of} from "rxjs";
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {FetchClientFilterModel} from "../models/fetch-client-filter.model";
import {
  FetchGroupStatusFilterModel
} from "../models/fetch-group-status-filter.model";
import {
  FetchMeasurementsFilterModel
} from "../models/fetch-measurements-filter.model";
import {FetchCoachesFilterModel} from "../models/fetch-coaches-filter.model";
import {GenerateGroupNameModel} from "../models/generate-group-name.model";
import {VerifyGroupNameModel} from "../models/verify-group-name.model";
import {AddGroupDetailsModel} from "../models/add-group-details.model";
import {UpdateGroupDetailsModel} from "../models/update-group-details.model";
import {FetchGroupDetailsModel} from "../models/fetch-group-details.model";
import {FetchSessionListModel} from "../models/fetch-session-list.model";
import {Router} from "@angular/router";
import {roles, roleUrlMap} from "../../../shared/constants/common.constants";
import {CommonService} from "../../../shared/services/common.service";
import {Location} from "@angular/common";
import {FetchClientDetailsModel} from "../models/fetch-client-details.model";
import {DeleteGroupModel} from "../models/delete-group.model";
import {profile} from "../../../shared/core/shared.selectors";
import {error} from "@angular/compiler-cli/src/transformers/util";

@Injectable()
export class GroupDetailsEffects {

  fetchClientFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchClientFilterRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.groupDetailsService.fetchClientsFilter(payload).pipe(
          map((response: FetchClientFilterModel['response']) => {
            const {data, message} = response;

            return FetchClientFilterSuccess({
              pageNumber: payload.pageNumber,
              clientFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchClientFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchClientFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchClientFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchGroupStatusFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchGroupStatusFilterRequest),
      mergeMap(() =>
        this.groupDetailsService.fetchGroupStatusFilter().pipe(
          map((response: FetchGroupStatusFilterModel['response']) => {
            const {data, message} = response;

            return FetchGroupStatusFilterSuccess({
              groupStatusFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchGroupStatusFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchGroupStatusFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchGroupStatusFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchMeasurementsFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchMeasurementsFilterRequest),
      mergeMap(() =>
        this.groupDetailsService.fetchMeasurementsFilter().pipe(
          map((response: FetchMeasurementsFilterModel['response']) => {
            const {data, message} = response;

            return FetchMeasurementsFilterSuccess({
              measurementsFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchMeasurementsFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchMeasurementsFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchMeasurementsFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchCoachesFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchCoachesFilterRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.groupDetailsService.fetchCoachesFilter(payload).pipe(
          map((response: FetchCoachesFilterModel['response']) => {
            const {data, message} = response;

            return FetchCoachesFilterSuccess({
              coachesFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchCoachesFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchCoachesFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchCoachesFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  generateGroupName$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(GenerateGroupNameRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: GenerateGroupNameModel['request']) =>
        this.groupDetailsService.generateGroupName(payload).pipe(
          map((response: GenerateGroupNameModel['response']) => {
            const {data, message} = response;
            const name = data.name;

            return GenerateGroupNameSuccess({
              name,
              message
            });
          }),
          catchError(() => {
            return of(GenerateGroupNameFailure());
          }),
          tap((action) => {
            if (action.type === GenerateGroupNameSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === GenerateGroupNameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  verifyGroupName$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(VerifyGroupNameRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: VerifyGroupNameModel['request']) =>
        this.groupDetailsService.verifyGroupName(payload).pipe(
          map((response: VerifyGroupNameModel['response']) => {
            const {data, message} = response;
            const isAvailable = data.isAvailable;

            return VerifyGroupNameSuccess({
              isAvailable,
              message
            });
          }),
          catchError(() => {
            return of(VerifyGroupNameFailure());
          }),
          tap((action) => {
            if (action.type === VerifyGroupNameSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === VerifyGroupNameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  addGroupDetails: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(AddGroupDetailsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.groupDetailsService.addGroupDetails(payload).pipe(
          map((response: AddGroupDetailsModel['response']) => {
            const {data, message} = response;
            const groupDetails = data;

            return AddGroupDetailsSuccess({
              groupDetails,
              message
            });
          }),
          catchError(() => {
            return of(AddGroupDetailsFailure());
          }),
          tap((action) => {
            if (action.type === AddGroupDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.groupDetailsService.resetForm.next();

            } else if (action.type === AddGroupDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  updateGroupDetails: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateGroupDetailsRequest),
      map((action: any) => {
        const payload = JSON.parse(JSON.stringify(action.payload))
        const sessionId = payload?.sessionId;
        const reSchedule = payload?.reSchedule;
        delete payload.sessionId;
        delete payload.reSchedule;
        return {payload, sessionId, reSchedule};
      }),
      mergeMap((payload) =>
        this.groupDetailsService.updateGroupDetails(payload.payload).pipe(
          map((response: UpdateGroupDetailsModel['response']) => {
            const {data, message} = response;
            const groupDetails = data;

            return UpdateGroupDetailsSuccess({
              groupDetails,
              message
            });
          }),
          catchError(() => {
            return of(UpdateGroupDetailsFailure());
          }),
          tap((action) => {
            if (action.type === UpdateGroupDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (payload?.sessionId) {
                this.location.back();
              }
              if (payload?.reSchedule) {
                this.router.navigate([`/stanford-admin/group/${payload.payload.groupId}/sessions`], {
                  queryParams: {
                    backToPopup: true,
                    sessionId: payload?.reSchedule
                  }
                });
              }
              this.commonService.notification(
                action.message,
                'success'
              );

              this.groupDetailsService.disableForm.next();

            } else if (action.type === UpdateGroupDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchGroupDetails: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchGroupDetailsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchGroupDetailsModel['request']) =>
        this.groupDetailsService.fetchGroupDetails(payload).pipe(
          map((response: FetchGroupDetailsModel['response']) => {
            const {data, message} = response;
            const groupDetails = data;
            return FetchGroupDetailsSuccess({
              groupDetails,
              message
            });
          }),
          catchError(() => {
            return of(FetchGroupDetailsFailure());
          }),
          tap((action) => {
            if (action.type === FetchGroupDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchGroupDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchSessionList: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSessionListRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap((payload: FetchSessionListModel['request']) =>
        this.groupDetailsService.fetchSessionList().pipe(
          map((response: FetchSessionListModel['response']) => {
            const {data, message} = response;
            const sessionList = data;

            return FetchSessionListSuccess({
              sessionList,
              message
            });
          }),
          catchError(() => {
            return of(FetchSessionListFailure());
          }),
          tap((action) => {
            if (action.type === FetchSessionListSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchSessionListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  createGroup: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateGroupRequest),
      map((action) => {
        return action;
      }),
      mergeMap(({payload, role}) =>
        this.groupDetailsService.createGroup(payload).pipe(
          map((response) => {
            const {message} = response;
            return CreateGroupSuccess({
              message
            });
          }),
          catchError(() => {
            return of(CreateGroupFailure());
          }),
          tap((action) => {
            if (action.type === CreateGroupSuccess.type) {
              // Code to execute on API Success Action dispatch
              let selectedDashboard: number;
              if (role === roles.superAdmin) {
                selectedDashboard = 2
                this.router.navigate([`${roleUrlMap[role]}/dashboard`], {
                  queryParams: {select: selectedDashboard}
                });
              } else if (role === roles.clientAdmin) {
                selectedDashboard = 1
                this.router.navigate([`${roleUrlMap[role]}/dashboard`], {
                  queryParams: {select: selectedDashboard}
                });
              } else if (role === roles.coach) {
                this.router.navigate([`${roleUrlMap[role]}/my-groups`])
              }
              this.commonService.notification(action.message, 'success')

            } else if (action.type === CreateGroupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  createDuplicateGroup: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SetDuplicateGroupRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchGroupDetailsModel['request']) =>
        this.groupDetailsService.createDuplicateGroup(payload).pipe(
          map((response: FetchGroupDetailsModel['response']) => {
            const {data, message} = response;
            const groupDetails = data;

            return SetDuplicateGroupSuccess({
              groupDetails,
              message
            });
          }),
          catchError((error) => {
            return of(SetDuplicateGroupFailure());
          }),
          tap((action) => {
            if (action.type === SetDuplicateGroupSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.select(profile)
                .subscribe(profile => {
                  this.router.navigate([`/${roleUrlMap[profile.role]}/group/duplicate`]);
                }).unsubscribe();

            } else if (action.type === SetDuplicateGroupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchGroupName: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchGroupNameRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchGroupDetailsModel['request']) =>
        this.groupDetailsService.fetchGroupName(payload).pipe(
          map((response: FetchGroupDetailsModel['response']) => {
            const {data, message} = response;

            return FetchGroupNameSuccess({
              name: data.name,
              startDate: data.startDate,
              message
            });
          }),
          catchError(() => {
            return of(FetchGroupNameFailure());
          }),
          tap((action) => {
            if (action.type === FetchGroupNameSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchGroupNameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchClientDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchClientDetailsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.groupDetailsService.fetchClientDetails(payload).pipe(
          map((response: FetchClientDetailsModel['response']) => {
            const {data, message} = response;

            return FetchClientDetailsSuccess({
              clientDetails: data
            });
          }),
          catchError(() => {
            return of(FetchClientDetailsFailure());
          }),
          tap((action) => {
            if (action.type === FetchClientDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchClientDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  deleteGroup$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteGroupRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload) =>
        this.groupDetailsService.deleteGroup(payload).pipe(
          map((response: DeleteGroupModel['response']) => {
            const {message} = response;
            return DeleteGroupSuccess({
              message,
              refresh: payload.refresh
            });
          }),
          catchError(() => {
            return of(DeleteGroupFailure());
          }),
          tap((action) => {
            if (action.type === DeleteGroupSuccess.type) {
              // Code to execute on API Success Action dispatch

              if (payload.refresh) {
                window.location.reload();
              } else {
                this.commonService.closeDialog();
                const urlPrefix = this.router.url.split('/')[1];
                this.router.navigate([urlPrefix]);
              }
              this.commonService.notification(
                action.message,
                'success'
              );

            } else if (action.type === DeleteGroupFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private groupDetailsService: GroupDetailsService,
    private actions: Actions,
    private router: Router,
    private commonService: CommonService,
    private location: Location
  ) {
  }
}
